import { gql } from '@apollo/client';
import DocumentViewer from '@orn/shared-web/components/DocumentViewer';
import { DocumentCategory, getDocumentCategory } from '@orn/shared-web/components/DocumentViewer/documentUtils';
import SlideoutMenu from '@orn/shared-web/components/SlideoutMenu';
import { FileIcon, ImageIcon, ZoomInIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { BillSource } from '../../../__generated__/globalTypes';
import { formatDateAtTime } from '../../../utils/formatting';

import type { AttachmentRowDocumentFragment as AttachmentDoc } from './__generated__/AttachmentRow.gql';
import type { IViewerDocument, IViewerMetadata } from '@orn/shared-web/components/DocumentViewer/documentUtils';

const AttachmentRowDocumentFragment = gql`
  fragment AttachmentRowDocument on Document {
    id
    name
    mimeType
    sequenceNumber
    assetUrl
    previewUrl
    emailMetadata {
      originalFrom
      originalTo
      originalSubject
    }
  }
`;

interface IAttachmentRowBill {
  source: BillSource;
  senderIdentifier?: string | null;
  createdAt?: string | null;
  documents: ReadonlyArray<AttachmentDoc>;
}

interface IAttachmentRowProps {
  bill: IAttachmentRowBill;
}

const MAX_PREVIEWS = 3;

function getFallbackIcon(category: DocumentCategory) {
  switch (category) {
    case DocumentCategory.IMAGE:
      return <ImageIcon className="text-tertiary size-4 shrink-0" />;
    case DocumentCategory.PDF:
      return <FileIcon className="text-tertiary size-4 shrink-0" />;
    default:
      return <FileIcon className="text-quaternary size-4 shrink-0" />;
  }
}

function DocumentFallback({ doc }: { doc: IViewerDocument }) {
  return (
    <div className="border-primary bg-secondary flex size-8 shrink-0 items-center justify-center rounded border">
      {getFallbackIcon(getDocumentCategory(doc.mimeType))}
    </div>
  );
}

function DocumentThumbnail({ doc }: { doc: IViewerDocument }) {
  if (doc.previewUrl) {
    return <img src={doc.previewUrl} alt="" className="border-primary size-8 shrink-0 rounded border object-cover" />;
  }
  return <DocumentFallback doc={doc} />;
}

function sortByPreviewFirst(docs: ReadonlyArray<IViewerDocument>): IViewerDocument[] {
  return [...docs].sort((a, b) => {
    const aHasPreview = a.previewUrl ? 0 : 1;
    const bHasPreview = b.previewUrl ? 0 : 1;
    return aHasPreview - bHasPreview;
  });
}

function buildMetadata(bill: IAttachmentRowBill): IViewerMetadata {
  const sourceLabel = bill.source === BillSource.Email ? 'Email' : bill.source === BillSource.Sms ? 'SMS' : 'Manual';
  return {
    source: sourceLabel,
    senderIdentifier: bill.senderIdentifier ?? null,
    date: bill.createdAt ? formatDateAtTime(bill.createdAt) : null,
  };
}

const AttachmentRow = ({ bill }: IAttachmentRowProps) => {
  const [isViewerOpen, setIsViewerOpen] = React.useState(false);

  const metadata = React.useMemo(() => buildMetadata(bill), [bill]);
  const sortedDocuments = React.useMemo(() => sortByPreviewFirst(bill.documents), [bill.documents]);

  if (bill.documents.length === 0) {
    return null;
  }

  const visible = sortedDocuments.slice(0, MAX_PREVIEWS);
  const overflow = bill.documents.length - MAX_PREVIEWS;

  return (
    <>
      <SlideoutMenu.Row
        icon={<ZoomInIcon className="size-4.5 shrink-0" />}
        title="View bills"
        onClick={() => setIsViewerOpen(true)}
      >
        <div className="flex items-center gap-1.5">
          {visible.map((doc) => (
            <DocumentThumbnail key={doc.id} doc={doc} />
          ))}
          {overflow > 0 && <span className="text-tertiary text-sm font-medium">+{overflow}</span>}
        </div>
      </SlideoutMenu.Row>
      <DocumentViewer
        documents={bill.documents}
        metadata={metadata}
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
      />
    </>
  );
};

export default Object.assign(AttachmentRow, { Fragment: AttachmentRowDocumentFragment });
